import React, { useState, useEffect } from "react";
import { Card, Typography, Row, Col, Switch, Button, message, Spin, Image } from "antd";
import { useSelector } from "react-redux";
import clientBillingApi from "../store/api";
import "./PlansPage.css";

const { Title, Text } = Typography;

const PlansPage = () => {
  const authUser = useSelector((state) => state.auth.user);
  const clientTierId = authUser?.client?.tierId;
  const currentTier = authUser?.client?.tier;

  const [tiers, setTiers] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [selectedTierId, setSelectedTierId] = useState(clientTierId);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [entitlementsLoading, setEntitlementsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Track entitlement states per tier: { tierId: { entitlementId: boolean } }
  const [tierEntitlements, setTierEntitlements] = useState({});

  useEffect(() => {
    fetchTiers();
    fetchEntitlements();
  }, []);

  useEffect(() => {
    if (clientTierId) {
      setSelectedTierId(clientTierId);
    }
  }, [clientTierId]);

  const fetchTiers = async () => {
    try {
      setTiersLoading(true);
      const response = await clientBillingApi.getTiers();
      setTiers(response || []);
    } catch (error) {
      console.error("Error fetching tiers:", error);
      message.error("Failed to load plans");
      setTiers([]);
    } finally {
      setTiersLoading(false);
    }
  };

  const fetchEntitlements = async () => {
    try {
      setEntitlementsLoading(true);
      const response = await clientBillingApi.getClientEntitlements();
      setEntitlements(response || []);

      // Initialize tier entitlements state - create a copy for each tier
      const initialState = {};
      (response || []).forEach((ent) => {
        initialState[ent.id] = ent.client_is_active || false;
      });
      setTierEntitlements({ [clientTierId]: initialState });
    } catch (error) {
      console.error("Error fetching entitlements:", error);
      message.error("Failed to load entitlements");
      setEntitlements([]);
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const handleEntitlementToggle = (tierId, entitlementId, checked) => {
    setTierEntitlements((prev) => ({
      ...prev,
      [tierId]: {
        ...(prev[tierId] || {}),
        [entitlementId]: checked
      }
    }));
  };

  const handleTierSelect = (tierId) => {
    setSelectedTierId(tierId);

    // Initialize entitlements for this tier if not already done
    if (!tierEntitlements[tierId]) {
      const tierData = tiers.find((t) => t.id === tierId);
      if (tierData) {
        const tierEntIds = (tierData.entitlements || []).map((e) => e.id);
        const newTierEnts = {};
        tierEntIds.forEach((entId) => {
          // Default to true for tier's entitlements
          newTierEnts[entId] = true;
        });
        setTierEntitlements((prev) => ({
          ...prev,
          [tierId]: newTierEnts
        }));
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // First, update the tier if changed
      if (selectedTierId !== clientTierId) {
        await clientBillingApi.updateClientTier(selectedTierId);
        message.success("Tier updated successfully");

        // Sync tier entitlements
        await clientBillingApi.syncTierEntitlements();
      }

      // Then, update each entitlement that differs from the default
      const selectedTierEnts = tierEntitlements[selectedTierId] || {};
      const entitlementUpdates = entitlements.filter((ent) => {
        const currentState = selectedTierEnts[ent.id];
        const originalState = ent.client_is_active || false;
        return currentState !== undefined && currentState !== originalState;
      });

      for (const ent of entitlementUpdates) {
        await clientBillingApi.updateClientEntitlement(
          ent.id,
          selectedTierEnts[ent.id],
          ent.settings || {}
        );
      }

      message.success("Plan and entitlements saved successfully!");

      // Refresh data
      await fetchEntitlements();

      // Reload the page to update the user's client data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error saving plan:", error);
      message.error("Failed to save plan. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (selectedTierId !== clientTierId) return true;

    const selectedTierEnts = tierEntitlements[selectedTierId] || {};
    return entitlements.some((ent) => {
      const currentState = selectedTierEnts[ent.id];
      const originalState = ent.client_is_active || false;
      return currentState !== undefined && currentState !== originalState;
    });
  };

  const formatPrice = (cost, period) => {
    if (cost === 0 || cost === null || cost === undefined) {
      return "$0";
    }
    const periodText =
      period === "monthly" || period === 30
        ? "/month"
        : period === "weekly" || period === 7
        ? "/week"
        : period === "daily" || period === 1
        ? "/day"
        : period === "yearly"
        ? "/year"
        : "";
    return `$${cost}${periodText}`;
  };

  const formatEntitlementCost = (ent) => {
    if (!ent.cost || ent.cost === 0) return "";
    const termText = ent.term === 30 ? "/month" : ent.term === 0 ? "/ea" : "";
    return ` ($${ent.cost}${termText})`;
  };

  const renderTierCard = (tier, index) => {
    const isSelected = tier.id === selectedTierId;
    const isCurrentPlan = tier.id === clientTierId;
    const isPopular = index === 1; // Second column is "most popular"

    // Show only entitlements associated with THIS tier
    const tierEntitlementIds = (tier.entitlements || []).map((e) => e.id);
    const tierEntitlementsForTier = entitlements.filter((ent) =>
      tierEntitlementIds.includes(ent.id)
    );

    // Get the current toggle state for this tier
    const currentTierEnts = tierEntitlements[tier.id] || {};

    return (
      <Col span={6} key={tier.id}>
        <Card
          className={`plan-card ${isSelected ? "selected" : ""} ${
            isPopular ? "popular" : ""
          }`}
          onClick={() => handleTierSelect(tier.id)}
        >
          {isCurrentPlan && (
            <div className="current-plan-badge">
              <Text strong>Your current plan</Text>
            </div>
          )}
          {isPopular && (
            <div className="popular-badge">
              <Text strong>Most Popular</Text>
            </div>
          )}

          {tier.logo && (
            <div className="tier-image-container">
              <Image
                src={tier.logo.url}
                alt={tier.name}
                preview={false}
                className="tier-image"
              />
            </div>
          )}

          <Title level={4} className="tier-name">
            {tier.name}
          </Title>

          <Text type="secondary" className="tier-description">
            {tier.description || ""}
          </Text>

          <div className="tier-price">
            <Text strong className="price-text">
              {formatPrice(tier.cost, tier.period)}
            </Text>
            {tier.cost === 0 && <Text className="price-footnote">*</Text>}
          </div>

          <div className="entitlements-list">
            {tierEntitlementsForTier.map((ent) => (
              <div key={ent.id} className="entitlement-item">
                <div className="entitlement-info">
                  <Text>
                    {ent.name}
                    {formatEntitlementCost(ent) && (
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {formatEntitlementCost(ent)}
                      </Text>
                    )}
                  </Text>
                </div>
                <Switch
                  size="small"
                  checked={
                    currentTierEnts[ent.id] !== undefined
                      ? currentTierEnts[ent.id]
                      : ent.client_is_active || false
                  }
                  onChange={(checked) =>
                    handleEntitlementToggle(tier.id, ent.id, checked)
                  }
                />
              </div>
            ))}
          </div>
        </Card>
      </Col>
    );
  };

  const renderManagedServicesCard = () => {
    return (
      <Col span={6} key="managed">
        <Card className="plan-card managed-card">
          <Title level={4}>-or-</Title>
          <Title level={5}>Managed Services</Title>
          <Text>
            Want us to handle route generation, dispatching, operations for you? Need a
            custom integration with your existing ERP or CRM software? No problem!
          </Text>
          <div style={{ marginTop: "20px" }}>
            <Button type="primary" href="mailto:sales@flexio.com">
              Contact Us
            </Button>
          </div>
        </Card>
      </Col>
    );
  };

  if (tiersLoading || entitlementsLoading) {
    return (
      <div className="plans-page">
        <div className="content-wrapper">
          <Card>
            <Spin tip="Loading plans..." />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="plans-page">
      <div className="content-wrapper">
        <Card>
          <Title level={2}>Choose your plan</Title>
          {currentTier && (
            <div style={{ marginTop: "16px" }}>
              <Text strong style={{ fontSize: "16px" }}>
                Your current plan:{" "}
              </Text>
              <Text style={{ fontSize: "16px", color: "#1890ff" }}>
                {currentTier.name}
              </Text>
            </div>
          )}
        </Card>

        <div style={{ marginTop: "24px" }}>
          <Row gutter={[24, 24]}>
            {tiers.map((tier, index) => renderTierCard(tier, index))}
            {renderManagedServicesCard()}
          </Row>
        </div>

        {tiers.length > 0 && tiers[0]?.cost === 0 && (
          <Card style={{ marginTop: "24px" }}>
            <Text type="secondary" className="footnote">
              * The cost shown is for the base plan; it does not include any upgrades or
              optional usages, such as sending routes or packages to be fulfilled by
              Flexio, which may incur separate costs. Everything is visible in the Billing
              dashboard.
            </Text>
          </Card>
        )}

        <Card style={{ marginTop: "24px", textAlign: "right" }}>
          <Button
            type="primary"
            size="large"
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges()}
          >
            Save Changes
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default PlansPage;
